// utils/imageUrlHelper.js - Frontend
// Utilitaire pour normaliser les URLs d'images côté frontend avec Digital Ocean Spaces
// ✨ NOUVELLE FONCTIONNALITÉ : Optimisation WebP + Responsive pour réseaux 3G

/**
 * Génère des URLs d'images optimisées avec WebP et responsive
 * @param {string} imagePath - Chemin d'image original
 * @param {Object} options - Options d'optimisation
 * @param {number} options.width - Largeur maximale souhaitée (480, 768, 1200)
 * @param {string} options.quality - Qualité ('low', 'medium', 'high')
 * @param {string} options.format - Format souhaité ('webp', 'jpeg', 'auto')
 * @returns {Object} - URLs srcSet optimisées
 */
export function getOptimizedImageUrls(imagePath, options = {}) {
  const {
    width = null,
    quality = 'medium',
    format = 'auto',
  } = options;

  const baseUrl = normalizeImageUrl(imagePath);

  // Si pas d'image, retourner placeholder
  if (!imagePath || baseUrl.startsWith('data:image/svg')) {
    return {
      src: baseUrl,
      srcSet: null,
      webp: null,
    };
  }

  // Configuration selon qualité réseau
  const qualityParams = {
    low: { q: 40, sizes: [480] },
    medium: { q: 70, sizes: [480, 768] },
    high: { q: 90, sizes: [480, 768, 1200] },
  };

  const config = qualityParams[quality] || qualityParams.medium;
  const targetWidth = width || Math.max(...config.sizes);

  // Générer srcSet pour différentes résolutions
  const srcSet = config.sizes
    .filter(size => size <= targetWidth)
    .map(size => {
      // Pour Digital Ocean Spaces, ajouter paramètres d'optimisation si supporté
      // Note: Nécessite configuration CDN côté DigitalOcean
      const optimizedUrl = `${baseUrl}?w=${size}&q=${config.q}`;
      return `${optimizedUrl} ${size}w`;
    })
    .join(', ');

  // Générer version WebP (meilleure compression)
  const webpSrc = format === 'auto' || format === 'webp'
    ? `${baseUrl}?w=${targetWidth}&q=${config.q}&fm=webp`
    : null;

  return {
    src: `${baseUrl}?w=${targetWidth}&q=${config.q}`,
    srcSet: srcSet || null,
    webp: webpSrc,
    sizes: config.sizes,
  };
}

/**
 * Normalise une URL d'image pour utiliser Digital Ocean Spaces
 * @param {string} imagePath - Chemin d'image depuis la base de données
 * @param {string} baseUrl - URL de base de l'API (fallback)
 * @returns {string} - URL absolue normalisée
 */
export function normalizeImageUrl(imagePath, baseUrl) {
  // Pas d'image - retourner placeholder
  if (!imagePath || imagePath.trim() === '') {
    return getImagePlaceholder();
  }

  // Nettoyer les espaces
  const cleanPath = imagePath.trim();

  // Si c'est déjà une URL complète (Digital Ocean Spaces ou autre CDN)
  if (cleanPath.startsWith('http://') || cleanPath.startsWith('https://')) {
    return cleanPath;
  }

  // Configuration Digital Ocean Spaces
  const SPACES_BUCKET = 'elsa-gestion-storage';
  const SPACES_ENDPOINT = 'fra1.digitaloceanspaces.com';
  const SPACES_CDN_URL = process.env.REACT_APP_SPACES_CDN_URL;

  // Si le chemin contient déjà le dossier products ou commence par products/
  if (cleanPath.includes('/products/') || cleanPath.startsWith('products/')) {
    const spacesUrl = `https://${SPACES_BUCKET}.${SPACES_ENDPOINT}/${cleanPath}`;
    return SPACES_CDN_URL ? `${SPACES_CDN_URL}/${cleanPath}` : spacesUrl;
  }

  // Si c'est un chemin ancien format (uploads/image_produits/ ou juste nom du fichier)
  let normalizedPath;
  
  if (cleanPath.startsWith('uploads/image_produits/')) {
    // Ancien format: uploads/image_produits/filename.jpg → products/filename.jpg
    const filename = cleanPath.replace('uploads/image_produits/', '');
    normalizedPath = `products/${filename}`;
  } else if (cleanPath.startsWith('uploads/')) {
    // Autre format uploads: garde le chemin mais remplace uploads par le bon dossier
    normalizedPath = cleanPath.replace('uploads/', 'products/');
  } else {
    // Juste le nom du fichier: filename.jpg → products/filename.jpg
    normalizedPath = `products/${cleanPath}`;
  }

  // URL Digital Ocean Spaces
  const spacesUrl = `https://${SPACES_BUCKET}.${SPACES_ENDPOINT}/${normalizedPath}`;
  
  // Utiliser CDN si disponible
  return SPACES_CDN_URL ? `${SPACES_CDN_URL}/${normalizedPath}` : spacesUrl;
}

/**
 * Normalise une URL de logo de boutique pour utiliser Digital Ocean Spaces
 * @param {string} logoPath - Chemin du logo depuis la base de données
 * @param {string} baseUrl - URL de base de l'API (fallback)
 * @returns {string} - URL absolue normalisée
 */
export function normalizeStoreLogoUrl(logoPath, baseUrl) {
  // Pas de logo - retourner placeholder de boutique
  if (!logoPath || logoPath.trim() === '') {
    return getStorePlaceholder();
  }

  // Nettoyer les espaces
  const cleanPath = logoPath.trim();

  // Si c'est déjà une URL complète (Digital Ocean Spaces ou autre CDN)
  if (cleanPath.startsWith('http://') || cleanPath.startsWith('https://')) {
    return cleanPath;
  }

  // Configuration Digital Ocean Spaces
  const SPACES_BUCKET = 'elsa-gestion-storage';
  const SPACES_ENDPOINT = 'fra1.digitaloceanspaces.com';
  const SPACES_CDN_URL = process.env.REACT_APP_SPACES_CDN_URL;

  // Si le chemin contient déjà le dossier stores ou commence par stores/
  if (cleanPath.includes('/stores/') || cleanPath.startsWith('stores/')) {
    const spacesUrl = `https://${SPACES_BUCKET}.${SPACES_ENDPOINT}/${cleanPath}`;
    return SPACES_CDN_URL ? `${SPACES_CDN_URL}/${cleanPath}` : spacesUrl;
  }

  // Si c'est un chemin ancien format (uploads/stores/ ou juste nom du fichier)
  let normalizedPath;

  if (cleanPath.startsWith('uploads/stores/')) {
    // Ancien format: uploads/stores/filename.jpg → stores/filename.jpg
    const filename = cleanPath.replace('uploads/stores/', '');
    normalizedPath = `stores/${filename}`;
  } else if (cleanPath.startsWith('/uploads/stores/')) {
    // Format avec slash initial: /uploads/stores/filename.jpg → stores/filename.jpg
    const filename = cleanPath.replace('/uploads/stores/', '');
    normalizedPath = `stores/${filename}`;
  } else if (cleanPath.startsWith('uploads/')) {
    // Autre format uploads: garde le chemin mais remplace uploads par stores
    normalizedPath = cleanPath.replace('uploads/', 'stores/');
  } else {
    // Juste le nom du fichier: filename.jpg → stores/filename.jpg
    normalizedPath = `stores/${cleanPath}`;
  }

  // URL Digital Ocean Spaces
  const spacesUrl = `https://${SPACES_BUCKET}.${SPACES_ENDPOINT}/${normalizedPath}`;

  // Utiliser CDN si disponible, sinon fallback vers l'API locale
  if (SPACES_CDN_URL) {
    return `${SPACES_CDN_URL}/${normalizedPath}`;
  } else if (spacesUrl) {
    return spacesUrl;
  } else {
    // Fallback vers l'API locale
    return `${baseUrl}${cleanPath.startsWith('/') ? '' : '/'}${cleanPath}`;
  }
}

/**
 * Génère un placeholder spécifique aux boutiques
 * @returns {string} - URL SVG placeholder pour boutique
 */
export function getStorePlaceholder() {
  return "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KICA8cmVjdCB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIGZpbGw9IiNmMGYwZjAiLz4KICA8cGF0aCBkPSJNMTIgMTBoMTZ2MmgtMTZ6bTAgNGgxNnYyaC0xNnptMCA0aDE2djJoLTE2eiIgZmlsbD0iI2NjYyIvPgogIDxwYXRoIGQ9Ik0xMCA4aDE2YzEgMCAyIDEgMiAydjE2YzAgMS0xIDItMiAySDE2YzEgMC0yLTEtMi0yVjEwYzAtMS0xLTItMi0yeiIgZmlsbD0iIzk5OSIvPgogIDxwYXRoIGQ9Ik0yMCAyNGMtMiAwLTQtMi00LTRzMi00IDQtNCA0IDIgNCA0LTIgNC00IDR6IiBmaWxsPSIjNjY2Ii8+Cjwvc3ZnPgo=";
}

/**
 * Génère une URL placeholder en cas d'erreur d'image
 * @param {string} altText - Texte alternatif pour le placeholder
 * @returns {string} - URL SVG placeholder
 */
export function getImagePlaceholder(altText = 'Image') {
  return "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyIgZmlsbC1ydWxlPSJldmVub2RkIiBjbGlwLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0yNCAyNEgwVjBoMjR2MjR6Ii8+PHBhdGggZmlsbD0iI2ZmZiIgZD0iTTEyIDJDNi40NzcgMiAyIDYuNDc3IDIgMTJzNC40NzcgMTAgMTAgMTAgMTAtNC40NzcgMTAtMTBTMTcuNTIzIDIgMTIgMnptMCAxOGMtNC40MTEgMC04LTMuNTg5LTgtOHMzLjU4OS04IDgtOCA4IDMuNTg5IDggOC0zLjU4OSA4LTggOHoiLz48cGF0aCBmaWxsPSIjZmZmIiBkPSJNMTMgN2gtMnY0SDd2Mmg0djRoMnYtNGg0di0yaC00Vjd6Ii8+PC9zdmc+";
}

/**
 * Compresse/redimensionne une image côté client via <canvas>.
 * Zéro dépendance : APIs navigateur uniquement. Sert à réduire le poids
 * des photos prises au téléphone (caméra) avant l'envoi multipart, afin de
 * rester sous la limite de 10 Mo de DigitalOcean Spaces et d'accélérer
 * l'upload sur réseau mobile. En cas d'échec (ex : HEIC iOS non décodable
 * par canvas.toBlob), renvoie le fichier original (fallback silencieux).
 *
 * @param {File} file - Fichier image source
 * @param {Object} [options]
 * @param {number} [options.maxSize=1600] - Côté maximal (px) après redimensionnement
 * @param {number} [options.quality=0.8] - Qualité JPEG (0–1)
 * @param {string} [options.mimeType="image/jpeg"] - Type de sortie
 * @returns {Promise<File>} - Fichier compressé (ou original si échec)
 */
export async function compressImage(
  file,
  { maxSize = 1600, quality = 0.8, mimeType = 'image/jpeg' } = {}
) {
  if (!file || !file.type || !file.type.startsWith('image/')) return file;
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = reject;
      i.src = url;
    });
    const scale = Math.min(1, maxSize / Math.max(img.width, img.height));
    const w = Math.round(img.width * scale);
    const h = Math.round(img.height * scale);
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    canvas.getContext('2d').drawImage(img, 0, 0, w, h);
    const blob = await new Promise((resolve) =>
      canvas.toBlob(resolve, mimeType, quality)
    );
    if (!blob) return file; // HEIC / échec toBlob → original
    return new File([blob], file.name.replace(/\.\w+$/, '.jpg'), {
      type: mimeType,
    });
  } catch {
    return file;
  } finally {
    URL.revokeObjectURL(url);
  }
}
